import { Typography, Box, useMediaQuery } from "@mui/material";
import { useTheme } from '@mui/material/styles';

// Component for Variables content
const RequestVariables = () => {
    const theme = useTheme();
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
    return (
        <Box style={{
            display: 'flex', flexDirection: 'column', gap: '16px', flex: 1, overflowY: 'auto',
            p: 3
        }}>
            <Typography variant="body1" style={{ width: isMediumScreen ? '100%' : '40%' }}>
                <strong>Request Variables</strong>
                <br /><br />
                <p>
                    Request variables are defined specifically for a single request and are not shared across other requests or collections.
                    They are ideal for storing temporary values or data generated or modified during a particular request lifecycle.
                </p>
                <p>
                    HawkClient supports two types of request variables:
                </p>
                <ul>
                    <li><strong>Pre-request Variables:</strong> These are defined and used before a request is sent. You can use them to set dynamic headers, query parameters, or body values.</li>
                    <li><strong>Post-response Variables:</strong> These are set after the request is completed and can be populated using values from the response, such as tokens or IDs for chaining requests.</li>
                </ul>
                <p>
                    These variables can be referenced within the same request using the <code>{`{{variableName}}`}</code> syntax and have the highest precedence, overriding environment, collection, and global variables if the same key exists.
                </p>
            </Typography>
            <img
                src="/docs/variables.png"
                alt="validations"
                style={{
                    width: isMediumScreen ? '100%' : '60%',
                    // maxWidth: '800px',
                    height: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                    marginTop: '16px',
                }}
            />
            <Typography variant="body1" style={{ width: isMediumScreen ? '100%' : '40%' }}>
                <strong>Pre Request</strong>
                <p>
                    Pre-request variables are key-value pairs defined specifically for a single request and are evaluated right before the request is sent.
                    These are useful for dynamically setting headers, query parameters, or request bodies based on logic or previously defined variables.
                    They are not shared with any other requests and are scoped only to the current request.
                </p>

                <strong>Post Response</strong>
                <p>
                    Post-response variables allow you to extract data from the response and assign it to variables at a broader scope such as
                    <strong> Environment, Global,</strong> or <strong>Collection</strong>. Each entry includes:
                </p>
                <ul>
                    <li><strong>Scope:</strong> Where the variable should be saved (e.g., env, global, collection).</li>
                    <li><strong>Key:</strong> The name of the variable.</li>
                    <li><strong>Value/Expression:</strong> The data to store, which can be derived from the response object.</li>
                </ul>

                <strong>Supported Fields for Post Response Expressions:</strong>
                <p>You can use the following fields from the response to populate post-response variables dynamically:</p>
                <ul>
                    <li><code>response.body</code> – the parsed response body (JSON or text)</li>
                    <li><code>response.status</code> – HTTP status code</li>
                    <li><code>response.statusText</code> – status message text</li>
                    <li><code>response.headers</code> – headers returned from the server</li>
                </ul>

                <p>
                    You can also access nested values from a JSON response. For example, if the response body is:
                </p>
                <pre style={{ background: '#f6f8fa', padding: '8px', borderRadius: '4px' }}>
                    {`{
  "user": {
    "token": "abc123"
  }
}`}
                </pre>
                <p>
                    You can use <code>response.body.user.token</code> to extract and store the token value.
                </p>

                <p>
                    These extracted values can then be reused in subsequent requests within the same workspace, making it easy to chain requests or manage dynamic data across flows.
                </p>
            </Typography>
        </Box>
    )
};

export default RequestVariables;